import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FilePdf, PaperclipHorizontal, ArrowSquareOut } from 'phosphor-react';
import Header from '../components/Header';
import { Button } from '../components/Button';
import { Loader } from '../components/Loader';
import { useApi } from '../hooks/useApi';
import { Message, Conversation, Email, EmailIntakeDetail } from '../types/api';
import { ThreadHeader, ThreadContent, OrderSidePanel } from '../components/messages';
import { OrdersTray } from './OrdersTray';

interface ConversationDetailProps {
  conversationId: string;
  conversation?: Conversation; // Optional: pass conversation to avoid reload
  onBack: () => void;
  onNavigateToOrder?: (orderId: string) => void;
  onMenuClick?: () => void;
  isMasterDetail?: boolean; // If true, hide header and back button
  onConversationUpdate?: (conversation: Conversation) => void; // Callback when conversation is updated
}

export const ConversationDetail: React.FC<ConversationDetailProps> = ({
  conversationId,
  conversation: conversationProp,
  onBack,
  onNavigateToOrder,
  onMenuClick,
  isMasterDetail = false,
  onConversationUpdate
}) => {
  const api = useApi();
  const [messageInput, setMessageInput] = useState('');
  const [isAiMode, setIsAiMode] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [showCustomerOrdersBottomSheet, setShowCustomerOrdersBottomSheet] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  // API data states
  const [messages, setMessages] = useState<Message[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [threadType, setThreadType] = useState<'whatsapp' | 'email'>('whatsapp');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerOrders, setCustomerOrders] = useState<any[]>([]);
  const [isLoadingCustomerOrders, setIsLoadingCustomerOrders] = useState(false);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [showManualModeBottomSheet, setShowManualModeBottomSheet] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [conversationOrderId, setConversationOrderId] = useState<number | undefined>();
  const [conversationOrderStatus, setConversationOrderStatus] = useState<'pending' | 'ready' | undefined>();
  const [orderModalId, setOrderModalId] = useState<string | null>(null);
  const [emailIntakeDetail, setEmailIntakeDetail] = useState<EmailIntakeDetail | null>(null);

  // Load conversation and messages/emails from real API
  useEffect(() => {
    if (api.isLoading || !api.isAuthenticated) return;

    const loadConversation = async () => {
      if (!conversationId) return;

      try {
        setIsLoading(true);

        let conversation = conversationProp;
        if (!conversation) {
          // conversationId is a threadKey like "wa_5" or "em_3"
          const threads = await api.getThreads();
          const match = threads.find(t => t.id === conversationId);
          if (match) {
            conversation = {
              id: match.type === 'whatsapp' ? match.conversationId : match.emailIntakeId,
              customerPhone: match.type === 'whatsapp' ? match.customerPhone : match.customerEmail,
              customerName: match.customerName,
              lastMessage: match.lastMessage,
              lastMessageAt: match.lastMessageAt,
              status: 'active' as any,
              channel: match.type,
              orderId: match.orderId,
              orderStatus: match.orderStatus as any,
              ...(match.type === 'email' ? { emailIntakeId: match.emailIntakeId } : {}),
            } as any;
          }
        }

        if (!conversation) {
          setError('Conversación no encontrada');
          setIsLoading(false);
          return;
        }

        setCurrentConversation(conversation);
        setConversationOrderId(conversation.orderId);
        setConversationOrderStatus(conversation.orderStatus);

        const channel = conversation.channel || 'whatsapp';
        setThreadType(channel);

        if (channel === 'email') {
          const emailIntakeId = (conversation as any).emailIntakeId || conversation.id;
          const detail = await api.getEmailIntakeDetail(emailIntakeId);
          setEmailIntakeDetail(detail);
          setMessages([]);
          setEmails([]);
        } else {
          // Load manual mode from context
          let parsedContext = conversation.context;
          if (typeof conversation.context === 'string') {
            try { parsedContext = JSON.parse(conversation.context); } catch { parsedContext = {}; }
          }
          setIsAiMode(!(parsedContext?.manualMode === true));

          const msgs = await api.getConversationMessages(conversation.id, 100);
          setMessages(msgs);
          setEmails([]);
          setEmailIntakeDetail(null);
        }

        setError(null);
      } catch (err) {
        console.error('Error loading conversation:', err);
        setError('Error al cargar la conversación');
      } finally {
        setIsLoading(false);
      }
    };

    loadConversation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversationProp]);

  // Function to scroll to bottom of messages container
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when messages are loaded
  useEffect(() => {
    if (messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [messages]);

  // Load customer orders when opening customer orders bottom sheet
  useEffect(() => {
    // Don't fetch if authentication is still loading or user is not authenticated
    if (api.isLoading || !api.isAuthenticated) {
      return;
    }

    const loadCustomerOrders = async () => {
      if (!showCustomerOrdersBottomSheet || !currentConversation) return;

      try {
        setIsLoadingCustomerOrders(true);
        const phone = currentConversation.channel === 'whatsapp'
          ? (currentConversation.customerPhone || '')
          : '';

        if (!phone) {
          setCustomerOrders([]);
          return;
        }

        const orders = await api.getOrdersByCustomerPhone(phone);
        setCustomerOrders(orders);
      } catch (err) {
        console.error('Error loading customer orders:', err);
        setCustomerOrders([]);
      } finally {
        setIsLoadingCustomerOrders(false);
      }
    };
    
    loadCustomerOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustomerOrdersBottomSheet, currentConversation]);

  // Lock body scroll when bottom sheet is open
  useEffect(() => {
    if (showCustomerOrdersBottomSheet) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCustomerOrdersBottomSheet]);


  const handleModeChange = async (ai: boolean) => {
    // If changing to manual mode (false), show confirmation bottom sheet
    if (!ai && isAiMode) {
      setShowManualModeBottomSheet(true);
      return;
    }
    
    // If changing to AI mode (true), proceed directly
    await confirmModeChange(ai);
  };

  const confirmModeChange = async (ai: boolean) => {
    if (ai) {
      setShowTooltip(true);
      setTooltipMessage('Activa el chat manual a la izquierda para poder escribir');
      setTimeout(() => setShowTooltip(false), 3000);
    } else {
      setShowTooltip(false);
    }
    setIsAiMode(ai);
    
    // Update manual mode in backend
    if (currentConversation?.id) {
      try {
        const updatedConversation = await api.setManualMode(currentConversation.id, !ai); // !ai because ai=true means AI mode, so manualMode should be false
        
        let parsedContext = updatedConversation?.context;
        if (updatedConversation?.context && typeof updatedConversation.context === 'string') {
          try {
            parsedContext = JSON.parse(updatedConversation.context);
          } catch {
            parsedContext = {};
          }
        }
        const isManualMode = parsedContext?.manualMode === true;
        setIsAiMode(!isManualMode);
        
        // Update current conversation state with the response from backend
        if (updatedConversation) {
          setCurrentConversation(updatedConversation);
          // Notify parent component (AllChats) about the update
          if (onConversationUpdate) {
            onConversationUpdate(updatedConversation);
          }
        }
      } catch (err) {
        console.error('Error setting manual mode:', err);
        // Don't show error to user, just log it
      }
    }
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || isAiMode || !currentConversation?.id || isSendingMessage) return;

    setIsSendingMessage(true);
    const messageText = messageInput.trim();
    setMessageInput('');

    // Create optimistic message
    const optimisticMessage: Message = {
      id: Date.now(),
      conversationId: currentConversation.id,
      from: currentConversation.customerPhone || '',
      to: '',
      body: messageText,
      type: 'text',
      timestamp: new Date().toISOString(),
      isFromCustomer: false,
      senderType: 'store_admin' as any,
      aiResponse: false
    };

    setMessages(prev => [...prev, optimisticMessage]);
    
    // Scroll to bottom after adding optimistic message
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    try {
      const sentMessage = await api.sendManualMessage(currentConversation.id, messageText);
      // Replace optimistic message with real one
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticMessage.id ? sentMessage : msg
      ));
      
      // Scroll to bottom after replacing with real message
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
    } finally {
      setIsSendingMessage(false);
    }
  };

  const displayName = currentConversation?.customerName || currentConversation?.customerPhone || 'Chat';
  
  // Show loading or error state if conversation not loaded
  if (isLoading && !currentConversation) {
    return (
      <div className="flex flex-col h-full w-full bg-muted">
        {!isMasterDetail && <Header onMenuClick={onMenuClick} />}
        <div className="flex-1 flex items-center justify-center">
          <Loader />
        </div>
      </div>
    );
  }
  
  if (error && !currentConversation) {
    return (
      <div className="flex flex-col h-full w-full bg-muted">
        {!isMasterDetail && <Header onMenuClick={onMenuClick} />}
        <div className="flex-1 flex items-center justify-center">
          <p className="font-sans font-medium text-base text-text-subtle">{error}</p>
        </div>
      </div>
    );
  }
  
  if (!currentConversation) {
    return null;
  }

  return (
    <motion.div
      className={`flex flex-col relative w-full ${isMasterDetail ? 'h-full overflow-hidden' : 'h-screen overflow-y-auto'} bg-muted`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {/* Header - Only show if not in master-detail mode */}
      {!isMasterDetail && <Header className="shrink-0 w-full" onMenuClick={onMenuClick} />}

      {/* Thread Header (shows back button + title + orders button) */}
      <ThreadHeader
        displayName={displayName}
        onBack={onBack}
        onViewOrders={() => setShowCustomerOrdersBottomSheet(true)}
        onOrderBadgeClick={(orderId: number) => {
          setOrderModalId(`P-${orderId}`);
        }}
        orderId={conversationOrderId}
        orderStatus={conversationOrderStatus}
        isMasterDetail={isMasterDetail}
      />

      {/* Main Content */}
      <div className="flex flex-col flex-1 min-h-0 w-full">
        <div className="flex flex-col flex-1 min-h-0 w-full px-3">
          <div className="flex-1 rounded-xl overflow-hidden flex flex-col max-w-[680px] mx-auto w-full">
            {threadType === 'email' ? (
              /* Email intake detail view */
              <div className="flex-1 overflow-y-auto bg-background rounded-xl">
                {isLoading ? (
                  <div className="flex items-center justify-center h-32"><Loader /></div>
                ) : emailIntakeDetail ? (
                  <div className="p-5 flex flex-col gap-5">
                    {/* Header metadata */}
                    <div className="flex flex-col gap-2">
                      <p className="font-sans font-semibold text-base text-text">
                        {emailIntakeDetail.subject || '(Sin asunto)'}
                      </p>
                      <div className="flex flex-col gap-1 text-xs text-text-subtle">
                        <span><span className="font-medium">De:</span> {emailIntakeDetail.sender}</span>
                        <span><span className="font-medium">Para:</span> {emailIntakeDetail.recipient}</span>
                        <span><span className="font-medium">Recibido:</span> {new Date(emailIntakeDetail.created_at).toLocaleString('es-CL')}</span>
                      </div>
                    </div>

                    {/* Processing status */}
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        emailIntakeDetail.processing_status === 'processed' ? 'bg-green-100 text-green-800' :
                        emailIntakeDetail.processing_status === 'failed' ? 'bg-red-100 text-red-800' :
                        emailIntakeDetail.processing_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {emailIntakeDetail.processing_status === 'processed' ? 'Procesado' :
                         emailIntakeDetail.processing_status === 'failed' ? 'Error' :
                         emailIntakeDetail.processing_status === 'pending' ? 'Pendiente' : 'Ignorado'}
                      </span>
                      {emailIntakeDetail.error_message && (
                        <span className="text-xs text-red-600">{emailIntakeDetail.error_message}</span>
                      )}
                    </div>

                    {/* Attachments */}
                    {emailIntakeDetail.attachments.length > 0 && (
                      <div className="flex flex-col gap-2">
                        <p className="font-sans font-medium text-sm text-text">Archivos adjuntos</p>
                        <div className="flex flex-col gap-1.5">
                          {emailIntakeDetail.attachments.map(att => (
                            <div key={att.id} className="flex items-center gap-2 p-2.5 rounded-lg bg-muted border border-border">
                              {att.content_type === 'application/pdf' || att.filename.endsWith('.pdf') ? (
                                <FilePdf size={18} weight="bold" className="text-red-500 shrink-0" />
                              ) : (
                                <PaperclipHorizontal size={18} weight="bold" className="text-text-subtle shrink-0" />
                              )}
                              <span className="text-sm text-text truncate">{att.filename}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Linked order */}
                    {emailIntakeDetail.linkedOrder ? (
                      <div className="flex flex-col gap-2">
                        <p className="font-sans font-medium text-sm text-text">Pedido generado</p>
                        <button
                          className="flex items-center justify-between p-3 rounded-lg bg-muted border border-border hover:bg-black/5 transition-colors text-left"
                          onClick={() => setOrderModalId(`P-${emailIntakeDetail.linkedOrder!.id}`)}
                        >
                          <div className="flex flex-col gap-0.5">
                            <span className="font-sans font-medium text-sm text-text">
                              P-{emailIntakeDetail.linkedOrder.id}
                              {emailIntakeDetail.linkedOrder.customer_name && (
                                <span className="font-normal text-text-subtle"> — {emailIntakeDetail.linkedOrder.customer_name}</span>
                              )}
                            </span>
                            <span className="text-xs text-text-subtle">
                              ${Math.round(emailIntakeDetail.linkedOrder.total_amount * 1.19).toLocaleString('es-CL')} IVA inc.
                            </span>
                          </div>
                          <ArrowSquareOut size={16} className="text-text-subtle shrink-0" />
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-text-subtle">No se generó pedido desde este correo.</p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <p className="text-sm text-text-subtle">No se pudo cargar el correo.</p>
                  </div>
                )}
              </div>
            ) : (
              /* WhatsApp thread content */
              <ThreadContent
                messages={messages}
                emails={emails}
                threadType={threadType}
                isLoading={isLoading}
                error={error}
                isMasterDetail={isMasterDetail}
                messageInput={messageInput}
                setMessageInput={setMessageInput}
                isAiMode={isAiMode}
                isSendingMessage={isSendingMessage}
                onSendMessage={handleSendMessage}
                onModeChange={handleModeChange}
                onManualModeClick={() => setShowManualModeBottomSheet(true)}
                showTooltip={showTooltip}
                tooltipMessage={tooltipMessage}
              />
            )}
          </div>
        </div>
      </div>

      {orderModalId && (
        <div style={{ position: 'fixed', width: 0, height: 0, overflow: 'visible', zIndex: 0 }}>
          <OrdersTray
            initialOrderId={orderModalId}
            onInitialOrderClose={() => setOrderModalId(null)}
          />
        </div>
      )}

      {/* Order Side Panel - Shows all customer orders */}
      <OrderSidePanel
        isOpen={showCustomerOrdersBottomSheet}
        onClose={() => setShowCustomerOrdersBottomSheet(false)}
        orders={customerOrders}
        isLoading={isLoadingCustomerOrders}
        onOrderClick={(orderId: string) => {
          if (onNavigateToOrder) {
            onNavigateToOrder(orderId);
          }
        }}
      />

      {/* Manual Mode Confirmation Bottom Sheet Modal */}
      {showManualModeBottomSheet && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowManualModeBottomSheet(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" />
          
          {/* Bottom Sheet Content */}
          <motion.div
            className="relative bg-background w-full rounded-t-[24px]"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="max-w-[460px] mx-auto p-6">
              {/* Handle */}
              <div className="mx-auto mb-4 w-12 h-1 bg-gray-300 rounded-full" />
              
              {/* Title */}
              <h3 className="font-sans font-semibold text-xl text-text mb-2">
                Desactivar chat automático con IA
              </h3>
              
              {/* Description */}
              <p className="font-sans font-normal text-sm text-text-subtle mb-6">
                Para poder escribir se desactiva la atención automatizada con IA, pero puedes volver a activarla con los botones de abajo a la izquierda.
              </p>
              
              {/* Actions */}
              <div className="flex flex-col gap-3">
                <Button
                  variant="primary"
                  fullWidth
                  onClick={async () => {
                    setShowManualModeBottomSheet(false);
                    await confirmModeChange(false);
                  }}
                  className="py-4 rounded-[6px]"
                >
                  Desactivar chat automático
                </Button>
                <Button
                  variant="ghost"
                  fullWidth
                  onClick={() => setShowManualModeBottomSheet(false)}
                  className="py-3"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ConversationDetail;

